import { config } from '../config';

export interface CapturedFrame {
  canvas: HTMLCanvasElement;
  previewUrl: string;
}

export type CameraFacingMode = 'user' | 'environment';

export async function openCamera(
  video: HTMLVideoElement,
  facingMode: CameraFacingMode,
): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('当前浏览器不支持摄像头访问。');
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      facingMode: { ideal: facingMode },
      width: { ideal: config.camera.idealWidth },
      height: { ideal: config.camera.idealHeight },
    },
  });

  video.srcObject = stream;
  video.dataset.facingMode = facingMode;
  await video.play();
  return stream;
}

export function stopCamera(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop());
}

export function captureSquare(video: HTMLVideoElement, mirror: boolean): CapturedFrame {
  if (!video.videoWidth || !video.videoHeight) {
    throw new Error('摄像头画面尚未准备完成。');
  }

  const size = config.camera.captureSize;
  const sourceSize = Math.min(video.videoWidth, video.videoHeight);
  const sx = (video.videoWidth - sourceSize) / 2;
  const sy = (video.videoHeight - sourceSize) / 2;

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('无法创建图像处理画布。');

  if (mirror) {
    context.translate(size, 0);
    context.scale(-1, 1);
  }
  context.drawImage(video, sx, sy, sourceSize, sourceSize, 0, 0, size, size);

  return {
    canvas,
    previewUrl: canvas.toDataURL('image/jpeg', config.camera.jpegQuality),
  };
}

export async function fileToSquareCanvas(file: File): Promise<CapturedFrame> {
  if (!file.type.startsWith('image/')) {
    throw new Error('请选择图片文件。');
  }

  const bitmap = await createImageBitmap(file);
  const size = config.camera.captureSize;
  const sourceSize = Math.min(bitmap.width, bitmap.height);
  const sx = (bitmap.width - sourceSize) / 2;
  const sy = (bitmap.height - sourceSize) / 2;

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('无法创建图像处理画布。');
  context.drawImage(bitmap, sx, sy, sourceSize, sourceSize, 0, 0, size, size);
  bitmap.close();

  return {
    canvas,
    previewUrl: canvas.toDataURL('image/jpeg', config.camera.jpegQuality),
  };
}
